import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// Types
export interface ObservationData {
  Location_Selected: string;
  GPID: string;
  Status: string;
  New_Hire_Annual_or_Routine: string;
  Shift_Number: string;
  Role_Observed: string;
  Created_By: string;
  Modified_By: string;
  Period: number | null;
  Week: number | null;
  ID: number;
}

export interface ObservationRowData {
  id: number;
  type: string;
  employee: string;
  details: string;
  created: string;
  by: string;
  modifiedBy: string;
  period: string;
  action: string;
  observationNotes?: string;
  miscellaneousObservations?: string;
  followUpRequired?: boolean;
  [key: string]: any;
}

// Add error handling types
export interface ObservationError {
  id: string;
  apiName: string;
  message: string;
  timestamp: number;
  context?: any;
}

export interface CentralizedErrorState {
  errors: ObservationError[];
  activeError: ObservationError | null;
  isErrorModalVisible: boolean;
}

export interface FilterOptions {
  employee: FilterItem[];
  role: FilterItem[];
  observationType: FilterItem[];
  shift: FilterItem[];
}

export interface ObservationViewFilters {
  observationType?: FilterItem[];
  shiftNumber?: FilterItem[]; // Changed from Shift
  roleObserved?: FilterItem[]; // Changed from Role
  status?: FilterItem[]; // Changed from Progress
  createdBy?: FilterItem[]; // Changed from CreatedBy
  modifiedBy?: FilterItem[]; // Changed from ModifiedBy
  period?: FilterItem[]; // Changed from Period
  week?: FilterItem[]; // Changed from Week
}

export interface FilterItem {
  key: string;
  label: string;
  options: { id: string; displayText: string }[];
}

export interface ObservationQuestion {
  id: number;
  questionText: string;
  observationType: string;
  roleObserved: string;
}

interface ObservationState {
  observations: ObservationData[];
  observationRows: ObservationRowData[];
  filterOptions: FilterOptions;
  selectedFilters: {
    employee: string;
    employeeFirstName?: string;
    employeeLastName?: string;
    employeeGpid?: string;
    role: string;
    observationType: string;
    shift: string;
  };
  selectedObservationViewFilters: {
    observationType: string;
    shiftNumber: string;
    roleObserved: string;
    status: string;
    createdBy: string;
    modifiedBy: string;
    period: string;
    week: string;
  };
  observationMetrices: {
    completed: number;
    inProgress: number;
    total: number;
    weeklyCompletedPulseChecks: number;
    weeklyInProgressPulseChecks: number;
    currentPeriodCompletedObservations: number;
    currentWeekCompletedObservations: number;
  };
  observationQuestions: ObservationQuestion[];
  questionsLoaded: boolean;
  loading: boolean;
  error: string | null;
  successMessage: string | null;
  activeTab: string;
  selectedObservation: ObservationRowData | null;
  // Add centralized error handling state
  centralizedErrors: CentralizedErrorState;
}

const initialState: ObservationState = {
  observations: [],
  observationRows: [],
  filterOptions: {
    employee: [],
    role: [],
    observationType: [],
    shift: [],
  },
  selectedFilters: {
    employee: "",
    employeeFirstName: "",
    employeeLastName: "",
    employeeGpid: "",
    role: "",
    observationType: "",
    shift: "",
  },
  selectedObservationViewFilters: {
    observationType: "",
    shiftNumber: "",
    roleObserved: "",
    status: "",
    createdBy: "",
    modifiedBy: "",
    period: "",
    week: "",
  },
  observationMetrices: {
    completed: 0,
    inProgress: 0,
    total: 0,
    weeklyCompletedPulseChecks: 0,
    weeklyInProgressPulseChecks: 0,
    currentPeriodCompletedObservations: 0,
    currentWeekCompletedObservations: 0,
  },
  observationQuestions: [],
  questionsLoaded: false,
  loading: false,
  error: null,
  successMessage: null,
  activeTab: "previous-observations",
  selectedObservation: {
    id: 0,
    type: "",
    employee: "",
    details: "",
    created: "",
    by: "",
    modifiedBy: "",
    period: "",
    action: "",
    observationNotes: "",
    miscellaneousObservations: "",
    followUpRequired: false,
  },
  // Initialize centralized error state
  centralizedErrors: {
    errors: [],
    activeError: null,
    isErrorModalVisible: false,
  },
};

const observationSlice = createSlice({
  name: "observation",
  initialState,
  reducers: {
    setObservations: (state, action: PayloadAction<ObservationData[]>) => {
      state.observations = action.payload;
    },
    setObservationRows: (
      state,
      action: PayloadAction<ObservationRowData[]>
    ) => {
      state.observationRows = action.payload;
    },
    setFilterOptions: (state, action: PayloadAction<FilterOptions>) => {
      state.filterOptions = action.payload;
    },
    setSelectedFilters: (
      state,
      action: PayloadAction<{
        employee?: string;
        employeeFirstName?: string;
        employeeLastName?: string;
        employeeGpid?: string;
        role?: string;
        observationType?: string;
        shift?: string;
      }>
    ) => {
      state.selectedFilters = { ...state.selectedFilters, ...action.payload };
    },
    setObservationViewFilters: (
      state,
      action: PayloadAction<{
        observationType?: string;
        shiftNumber?: string;
        roleObserved?: string;
        status?: string;
        createdBy?: string;
        modifiedBy?: string;
        period?: string;
        week?: string;
      }>
    ) => {
      state.selectedObservationViewFilters = {
        ...state.selectedObservationViewFilters,
        ...action.payload,
      };
    },
    setObservationMetrices: (
      state,
      action: PayloadAction<{
        completed: number;
        inProgress: number;
        total: number;
        weeklyCompletedPulseChecks?: number;
        weeklyInProgressPulseChecks?: number;
        currentPeriodCompletedObservations: number;
        currentWeekCompletedObservations: number;
      }>
    ) => {
      const payload = action.payload;
      state.observationMetrices.completed = payload.completed;
      state.observationMetrices.inProgress = payload.inProgress;
      state.observationMetrices.total = payload.total;
      state.observationMetrices.weeklyCompletedPulseChecks =
        payload.weeklyCompletedPulseChecks ?? state.observationMetrices.weeklyCompletedPulseChecks;
      state.observationMetrices.weeklyInProgressPulseChecks =
        payload.weeklyInProgressPulseChecks ?? state.observationMetrices.weeklyInProgressPulseChecks;
      state.observationMetrices.currentPeriodCompletedObservations =
        payload.currentPeriodCompletedObservations;
      state.observationMetrices.currentWeekCompletedObservations =
        payload.currentWeekCompletedObservations;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setActiveTab: (state, action: PayloadAction<string>) => {
      state.activeTab = action.payload;
    },
    setSelectedObservation: (
      state,
      action: PayloadAction<ObservationRowData | null>
    ) => {
      state.selectedObservation = action.payload;
    },
    setObservationNotes: (state, action: PayloadAction<string>) => {
      if (state.selectedObservation) {
        state.selectedObservation = {
          ...state.selectedObservation,
          observationNotes: action.payload,
        };
      }
    },
    resetFilters: (state) => {
      state.selectedFilters = initialState.selectedFilters;
    },
    resetObservation: (state) => {
      state.selectedObservation = initialState.selectedObservation;
    },
    clearError: (state) => {
      state.error = null;
    },
    setSuccessMessage: (state, action: PayloadAction<string | null>) => {
      state.successMessage = action.payload;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    setObservationQuestions: (
      state,
      action: PayloadAction<ObservationQuestion[]>
    ) => {
      state.observationQuestions = action.payload;
      state.questionsLoaded = true;
    },
    // Memory management actions
    limitObservations: (state, action: PayloadAction<{ maxItems: number }>) => {
      const { maxItems } = action.payload;
      if (state.observations.length > maxItems) {
        // Keep most recent observations
        state.observations = state.observations.slice(-maxItems);
        console.log(`Observation cleanup: Limited to ${maxItems} most recent observations`);
      }
      if (state.observationRows.length > maxItems) {
        // Keep most recent observation rows
        state.observationRows = state.observationRows.slice(-maxItems);
        console.log(`Observation rows cleanup: Limited to ${maxItems} most recent rows`);
      }
    },
    clearImages: (state) => {
      // Clear any image references in observations
      state.observations = state.observations.map(obs => ({
        ...obs,
        imageUrl: undefined,
        images: undefined,
        attachments: undefined,
      }));
      console.log('Observation images cleared for memory optimization');
    },
    clearOldObservations: (state, action: PayloadAction<{ daysCutoff: number }>) => {
      const { daysCutoff } = action.payload;
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysCutoff);
      
      const originalCount = state.observations.length;
      state.observations = state.observations.filter(obs => {
        // Use the correct property based on the ObservationData interface
        const obsDate = new Date((obs as any).Created || (obs as any).created || '');
        return obsDate >= cutoffDate;
      });
      
      const removedCount = originalCount - state.observations.length;
      if (removedCount > 0) {
        console.log(`Observation cleanup: Removed ${removedCount} observations older than ${daysCutoff} days`);
      }
    },
    resetObservationState: (state) => {
      // Complete reset for memory cleanup
      Object.assign(state, initialState);
      console.log('Observation state reset for memory cleanup');
    },
    // Centralized Error Handling Actions
    addCentralizedError(state, action: PayloadAction<ObservationError>) {
      state.centralizedErrors.errors.push(action.payload);
      // Keep only the last 10 errors to prevent memory issues
      if (state.centralizedErrors.errors.length > 10) {
        state.centralizedErrors.errors = state.centralizedErrors.errors.slice(-10);
      }
    },
    showErrorModal(state, action: PayloadAction<ObservationError>) {
      state.centralizedErrors.activeError = action.payload;
      state.centralizedErrors.isErrorModalVisible = true;
    },
    hideErrorModal(state) {
      state.centralizedErrors.isErrorModalVisible = false;
      state.centralizedErrors.activeError = null;
    },
    setActiveError(state, action: PayloadAction<ObservationError | null>) {
      state.centralizedErrors.activeError = action.payload;
    },
    clearAllErrors(state) {
      state.centralizedErrors.errors = [];
      state.centralizedErrors.activeError = null;
      state.centralizedErrors.isErrorModalVisible = false;
    },
    removeErrorById(state, action: PayloadAction<string>) {
      state.centralizedErrors.errors = state.centralizedErrors.errors.filter(
        error => error.id !== action.payload
      );
      // If the active error was removed, hide the modal
      if (state.centralizedErrors.activeError?.id === action.payload) {
        state.centralizedErrors.activeError = null;
        state.centralizedErrors.isErrorModalVisible = false;
      }
    },
  },
});

export const {
  setObservations,
  setObservationRows,
  setFilterOptions,
  setSelectedFilters,
  setObservationViewFilters,
  setLoading,
  setError,
  setActiveTab,
  setSelectedObservation,
  setObservationMetrices,
  setObservationNotes,
  resetFilters,
  resetObservation,
  clearError,
  setSuccessMessage,
  clearSuccessMessage,
  setObservationQuestions,
  // Memory management actions
  limitObservations,
  clearImages,
  clearOldObservations,
  resetObservationState,
  // Centralized error handling actions
  addCentralizedError,
  showErrorModal,
  hideErrorModal,
  setActiveError,
  clearAllErrors,
  removeErrorById,
} = observationSlice.actions;

export default observationSlice.reducer;
